import { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Instagram, Youtube, Music2, Globe, Twitter, Facebook,
  Calendar, MapPin, ArrowLeft, ExternalLink, Share2,
  Headphones, PlayCircle, Disc3, Download, Clock, DollarSign,
  Award, Building2, Disc, FileText, Settings, Plane, CheckCircle2,
  Video, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import AgencyPublicView from '../components/dj/AgencyPublicView';
import { formatEventDate } from '../lib/dateUtils';

// TikTok icon component (not in lucide)
const TikTokIcon = ({ size = 24, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function DJPublicPage() {
  const { identifier } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [dj, setDj] = useState(null);
  const [agency, setAgency] = useState(null);
  const [roster, setRoster] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchDJProfile();
  }, [identifier]);

  // Canonicalize legacy /dj/{agency-slug} → /agencia/{agency-slug} (side-effect only).
  // Guard: only redirect when the loaded profile actually matches the current URL
  // identifier — otherwise clicking a DJ from an agency roster would flash-redirect
  // back to the agency page (stale `dj` state during transition).
  useEffect(() => {
    if (!dj) return;
    if ((dj.applicant_type || 'dj') !== 'agency') return;
    if (!dj.slug) return;
    if (!location.pathname.startsWith('/dj/')) return;
    if (identifier !== dj.slug && identifier !== dj.id) return;
    navigate(`/agencia/${dj.slug}`, { replace: true });
  }, [dj, location.pathname, identifier, navigate]);

  const fetchDJProfile = async () => {
    try {
      setLoading(true);
      // Reset stale state to avoid the previous profile leaking into the redirect
      // effect (e.g., agency page → click a roster DJ would flash-redirect back).
      setDj(null);
      setAgency(null);
      setRoster([]);
      const res = await api.get(`/dj/public/${identifier}`);
      setDj(res.data.dj);
      setAgency(res.data.agency || null);
      setRoster(res.data.roster || []);
      setUpcomingEvents(res.data.upcoming_events || []);
    } catch (error) {
      console.error('Error fetching DJ:', error);
      toast.error('DJ no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/api/s/dj/${dj?.slug || identifier}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: dj?.artistic_name,
          text: `${dj?.artistic_name} - Press Kit`,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Link copiado');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copiado');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Disc3 className="w-12 h-12 text-[#D4AF37] animate-spin" />
        </div>
      </div>
    );
  }

  if (!dj) {
    return (
      <div className="min-h-screen bg-[#030303]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <Headphones className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">DJ no encontrado</h1>
          <p className="text-white/60 mb-6">El perfil que buscás no existe o no está disponible.</p>
          <Link to="/" className="text-[#D4AF37] hover:underline">
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const socialLinks = [
    { key: 'instagram', icon: Instagram, url: dj.instagram, label: 'Instagram' },
    { key: 'facebook', icon: Facebook, url: dj.facebook, label: 'Facebook' },
    { key: 'twitter', icon: Twitter, url: dj.twitter, label: 'Twitter' },
    { key: 'tiktok', icon: TikTokIcon, url: dj.tiktok, label: 'TikTok' },
    { key: 'youtube', icon: Youtube, url: dj.youtube, label: 'YouTube' },
    { key: 'website', icon: Globe, url: dj.website, label: 'Website' },
  ].filter(s => s.url);

  const musicLinks = [
    { key: 'spotify', url: dj.spotify_url, label: 'Spotify', color: '#1DB954' },
    { key: 'soundcloud', url: dj.soundcloud_url, label: 'SoundCloud', color: '#FF5500' },
    { key: 'mixcloud', url: dj.mixcloud_url, label: 'Mixcloud', color: '#5000FF' },
    { key: 'beatport', url: dj.beatport_url, label: 'Beatport', color: '#94D500' },
    { key: 'bandcamp', url: dj.bandcamp_url, label: 'Bandcamp', color: '#1DA0C3' },
    { key: 'apple_music', url: dj.apple_music_url, label: 'Apple Music', color: '#FA243C' },
  ].filter(s => s.url);

  // Check if DJ has presskit content
  const hasCareerInfo = dj.residencies?.length || dj.festivals?.length || dj.labels?.length || dj.awards?.length;
  const hasReleases = dj.releases?.length > 0;
  const hasTechnicalInfo = dj.technical_rider || dj.rider_pdf_url || dj.equipment_details;
  const hasBookingInfo = dj.cache_min || dj.availability || dj.email_contact;
  // Valid gallery items (legacy DBs may contain typos / non-URLs)
  const validGallery = (dj.gallery_urls || []).filter(
    (u) => typeof u === 'string' && /^https?:\/\//i.test(u)
  );
  const hasGallery = validGallery.length > 0;
  const hasVideos = dj.video_urls?.length > 0;

  // Define tabs based on available content
  const tabs = [
    { id: 'about', label: 'Sobre mí', icon: Headphones },
    hasCareerInfo && { id: 'career', label: 'Trayectoria', icon: Award },
    hasReleases && { id: 'releases', label: 'Releases', icon: Disc },
    (hasGallery || hasVideos) && { id: 'media', label: 'Media', icon: ImageIcon },
    hasTechnicalInfo && { id: 'rider', label: 'Rider Técnico', icon: Settings },
    hasBookingInfo && { id: 'booking', label: 'Booking', icon: Calendar },
  ].filter(Boolean);

  // Agencies get a corporate public profile with their roster.
  // Legacy /dj/{agency-slug} URLs are canonicalized to /agencia/{slug} via useEffect above.
  if ((dj.applicant_type || 'dj') === 'agency') {
    return <AgencyPublicView agency={dj} roster={roster} identifier={identifier} />;
  }

  return (
    <div className="min-h-screen bg-[#030303] w-full">
      <SEO 
        title={`${dj.artistic_name} - Press Kit`}
        description={dj.bio?.substring(0, 160) || `${dj.artistic_name} - DJ & Producer`}
        image={dj.photo_url || dj.banner_url}
        url={`/dj/${dj.slug || identifier}`}
        type="profile"
      />
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative">
        {/* Banner */}
        <div className="h-[200px] sm:h-[280px] md:h-[380px] relative overflow-hidden">
          {dj.banner_url ? (
            <img 
              src={dj.banner_url} 
              alt={dj.artistic_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/30 via-[#1a1a1a] to-[#030303]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/60 to-transparent" />
        </div>

        {/* Profile Info */}
        <div className="max-w-6xl mx-auto px-4 -mt-20 sm:-mt-28 md:-mt-32 relative z-10">
          <div className="flex flex-col items-center sm:items-start sm:flex-row gap-4 sm:gap-6 sm:items-end">
            {/* Photo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full border-4 border-[#D4AF37]/30 overflow-hidden bg-zinc-900 flex-shrink-0 shadow-2xl shadow-[#D4AF37]/20"
            >
              {dj.photo_url ? (
                <img 
                  src={dj.photo_url} 
                  alt={dj.artistic_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
                  <Headphones className="w-12 h-12 sm:w-16 sm:h-16 text-[#D4AF37]/40" />
                </div>
              )}
            </motion.div>

            {/* Info */}
            <div className="flex-1 pb-4 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-3 mb-2">
                <Link to="/" className="text-white/60 hover:text-white transition-colors">
                  <ArrowLeft size={20} />
                </Link>
                <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium rounded">
                  PRESS KIT
                </span>
              </div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl sm:text-3xl md:text-5xl font-black text-white mb-2 sm:mb-3"
              >
                {dj.artistic_name}
              </motion.h1>
              
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1.5 text-white/60 text-xs sm:text-sm">
                {dj.city && (
                  <span className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#D4AF37]" />
                    {dj.city}
                  </span>
                )}
                {dj.genres?.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Music2 size={14} className="text-[#D4AF37]" />
                    {dj.genres.slice(0, 3).join(' / ')}
                  </span>
                )}
                {dj.experience_years && (
                  <span className="flex items-center gap-1.5">
                    <Clock size={14} className="text-[#D4AF37]" />
                    {dj.experience_years}+ años
                  </span>
                )}
              </div>

              {/* Represented by agency badge */}
              {agency && (
                <div className="flex justify-center sm:justify-start mt-3">
                  <Link
                    to={`/agencia/${agency.slug || ''}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/20 transition-colors"
                    data-testid="represented-by-badge"
                  >
                    {agency.photo_url ? (
                      <img src={agency.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <span className="w-5 h-5 rounded-full bg-[#D4AF37] flex items-center justify-center text-black text-[10px] font-black">
                        {(agency.name || 'A').charAt(0)}
                      </span>
                    )}
                    <span className="text-xs text-white/70">
                      Representado por <strong className="text-[#D4AF37]">{agency.name}</strong>
                    </span>
                  </Link>
                </div>
              )}

              {/* Social Links */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3 sm:mt-4">
                {socialLinks.map(social => (
                  <a
                    key={social.key}
                    href={social.url.startsWith('http') ? social.url : `https://${social.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                    title={social.label}
                  >
                    <social.icon size={16} className="text-white" />
                  </a>
                ))}
                <button
                  onClick={handleShare}
                  className="w-9 h-9 rounded-full bg-[#D4AF37] hover:bg-[#B8962E] flex items-center justify-center transition-all hover:scale-110"
                  title="Compartir Press Kit"
                  data-testid="share-presskit-btn"
                >
                  <Share2 size={16} className="text-black" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="sticky top-16 z-20 bg-[#030303]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={14} className="sm:hidden" />
                <tab.icon size={16} className="hidden sm:block" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="md:col-span-2 space-y-6 sm:space-y-8">
              {/* Bio */}
              {dj.bio && (
                <section>
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                    <Headphones size={18} className="text-[#D4AF37]" />
                    Biografía
                  </h2>
                  <p className="text-white/70 whitespace-pre-line leading-relaxed text-sm sm:text-base">{dj.bio}</p>
                </section>
              )}

              {/* Music Links */}
              {musicLinks.length > 0 && (
                <section>
                  <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                    <PlayCircle size={18} className="text-[#D4AF37]" />
                    Escuchá mi música
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                    {musicLinks.map(link => (
                      <a
                        key={link.key}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all hover:scale-[1.02] border border-white/5"
                      >
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${link.color}20` }}>
                          <PlayCircle size={16} style={{ color: link.color }} />
                        </div>
                        <span className="text-white text-xs sm:text-sm font-medium truncate">{link.label}</span>
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* Quick Stats */}
              <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {dj.experience_years && (
                  <div className="bg-white/5 rounded-xl p-3 sm:p-4 text-center border border-white/5">
                    <p className="text-2xl sm:text-3xl font-black text-[#D4AF37]">{dj.experience_years}+</p>
                    <p className="text-white/50 text-[10px] sm:text-xs mt-1">Años de experiencia</p>
                  </div>
                )}
                {dj.started_year && (
                  <div className="bg-white/5 rounded-xl p-3 sm:p-4 text-center border border-white/5">
                    <p className="text-2xl sm:text-3xl font-black text-[#D4AF37]">{dj.started_year}</p>
                    <p className="text-white/50 text-[10px] sm:text-xs mt-1">Desde</p>
                  </div>
                )}
                {dj.set_duration_min && dj.set_duration_max && (
                  <div className="bg-white/5 rounded-xl p-3 sm:p-4 text-center border border-white/5">
                    <p className="text-xl sm:text-2xl font-black text-[#D4AF37]">{dj.set_duration_min}-{dj.set_duration_max}</p>
                    <p className="text-white/50 text-[10px] sm:text-xs mt-1">Min set (min)</p>
                  </div>
                )}
                {dj.genres?.length > 0 && (
                  <div className="bg-white/5 rounded-xl p-3 sm:p-4 text-center border border-white/5">
                    <p className="text-2xl sm:text-3xl font-black text-[#D4AF37]">{dj.genres.length}</p>
                    <p className="text-white/50 text-[10px] sm:text-xs mt-1">Géneros</p>
                  </div>
                )}
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Upcoming Events */}
              {upcomingEvents.length > 0 && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/5">
                  <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                    <Calendar size={16} className="text-[#D4AF37]" />
                    Próximos eventos
                  </h3>
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 3).map(event => (
                      <Link
                        key={event.id}
                        to={`/evento/${event.slug || event.id}`}
                        className="block p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <p className="text-white font-medium text-sm truncate">{event.title}</p>
                        <p className="text-white/50 text-xs mt-1">
                          {formatEventDate(event.date, "d MMM yyyy")}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Contact */}
              {(dj.email_contact || dj.phone) && (
                <div className="bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-xl p-5 border border-[#D4AF37]/30">
                  <h3 className="text-base font-bold text-white mb-3">Contacto rápido</h3>
                  {dj.email_contact && (
                    <a 
                      href={`mailto:${dj.email_contact}?subject=Booking ${dj.artistic_name}`}
                      className="flex items-center justify-center gap-2 w-full py-3 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold rounded-lg transition-colors"
                    >
                      Enviar email
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Career Tab */}
        {activeTab === 'career' && hasCareerInfo && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            {/* Residencies */}
            {dj.residencies?.length > 0 && (
              <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Building2 size={18} className="text-[#D4AF37]" />
                  Residencias
                </h2>
                <div className="space-y-2">
                  {dj.residencies.map((venue, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                      <CheckCircle2 size={16} className="text-[#D4AF37] flex-shrink-0" />
                      <span className="text-white/80 text-sm sm:text-base">{venue}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Festivals */}
            {dj.festivals?.length > 0 && (
              <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Music2 size={18} className="text-[#D4AF37]" />
                  Festivales
                </h2>
                <div className="flex flex-wrap gap-2">
                  {dj.festivals.map((festival, i) => (
                    <span key={i} className="px-3 py-1.5 bg-white/10 text-white/80 text-xs sm:text-sm rounded-full">
                      {festival}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Labels */}
            {dj.labels?.length > 0 && (
              <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Disc size={18} className="text-[#D4AF37]" />
                  Sellos Discográficos
                </h2>
                <div className="flex flex-wrap gap-2">
                  {dj.labels.map((label, i) => (
                    <span key={i} className="px-3 py-1.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs sm:text-sm rounded-full border border-[#D4AF37]/30">
                      {label}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Awards */}
            {dj.awards?.length > 0 && (
              <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Award size={18} className="text-[#D4AF37]" />
                  Premios y Reconocimientos
                </h2>
                <div className="space-y-2">
                  {dj.awards.map((award, i) => (
                    <div key={i} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
                      <Award size={16} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span className="text-white/80 text-sm sm:text-base">{award}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Releases Tab */}
        {activeTab === 'releases' && hasReleases && (
          <div className="space-y-4 sm:space-y-6">
            <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <Disc size={20} className="text-[#D4AF37]" />
              Discografía
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {dj.releases.map((release, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white/5 rounded-xl p-4 sm:p-5 border border-white/5 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-gradient-to-br from-[#D4AF37]/30 to-[#1a1a1a] flex items-center justify-center flex-shrink-0">
                      <Disc size={24} className="text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-sm sm:text-base truncate">{release.title}</h3>
                      {release.label && (
                        <p className="text-white/50 text-xs sm:text-sm">{release.label}</p>
                      )}
                      {release.year && (
                        <p className="text-[#D4AF37] text-xs sm:text-sm font-medium">{release.year}</p>
                      )}
                    </div>
                  </div>
                  {release.link && (
                    <a
                      href={release.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 mt-3 sm:mt-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
                    >
                      <PlayCircle size={16} />
                      Escuchar
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (hasGallery || hasVideos) && (
          <div className="space-y-6 sm:space-y-8">
            {/* Gallery */}
            {hasGallery && (
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <ImageIcon size={20} className="text-[#D4AF37]" />
                  Fotos promocionales
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                  {validGallery.map((url, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                      className="aspect-square rounded-lg sm:rounded-xl overflow-hidden cursor-pointer hover:ring-2 ring-[#D4AF37] transition-all bg-zinc-900"
                      onClick={() => setSelectedImage(url)}
                    >
                      <img
                        src={url}
                        alt={`${dj.artistic_name} photo ${i + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.style.display = 'none';
                          }
                        }}
                      />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

            {/* Videos */}
            {hasVideos && (
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Video size={20} className="text-[#D4AF37]" />
                  Videos
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {dj.video_urls.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 sm:p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors border border-white/5"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                        <PlayCircle size={20} className="text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">Video {i + 1}</p>
                        <p className="text-white/50 text-xs truncate">{url}</p>
                      </div>
                      <ExternalLink size={16} className="text-white/40 flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Technical Rider Tab */}
        {activeTab === 'rider' && hasTechnicalInfo && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            {/* Rider Info */}
            {(dj.technical_rider || dj.rider_pdf_url) && (
              <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <FileText size={18} className="text-[#D4AF37]" />
                  Rider Técnico
                </h2>
                {dj.technical_rider && (
                  <p className="text-white/70 whitespace-pre-line mb-4 text-sm sm:text-base">{dj.technical_rider}</p>
                )}
                {dj.rider_pdf_url && (
                  <a
                    href={dj.rider_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 py-3 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold rounded-lg transition-colors text-sm sm:text-base"
                  >
                    <Download size={18} />
                    Descargar Rider PDF
                  </a>
                )}
              </section>
            )}

            {/* Equipment */}
            {dj.equipment_details && (
              <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Settings size={18} className="text-[#D4AF37]" />
                  Equipamiento
                  {dj.equipment_own && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full ml-auto">
                      Equipo propio
                    </span>
                  )}
                </h2>
                <p className="text-white/70 whitespace-pre-line text-sm sm:text-base">{dj.equipment_details}</p>
              </section>
            )}

            {/* Set Duration */}
            {(dj.set_duration_min || dj.set_duration_max) && (
              <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-[#D4AF37]" />
                  Duración de Sets
                </h2>
                <div className="flex items-center justify-center gap-3 sm:gap-4">
                  <div className="text-center">
                    <p className="text-2xl sm:text-3xl font-black text-[#D4AF37]">{dj.set_duration_min || '?'}</p>
                    <p className="text-white/50 text-xs sm:text-sm">Min</p>
                  </div>
                  <span className="text-white/30 text-xl sm:text-2xl">→</span>
                  <div className="text-center">
                    <p className="text-2xl sm:text-3xl font-black text-[#D4AF37]">{dj.set_duration_max || '?'}</p>
                    <p className="text-white/50 text-xs sm:text-sm">Max</p>
                  </div>
                  <span className="text-white/50 text-sm ml-2">minutos</span>
                </div>
              </section>
            )}
          </div>
        )}

        {/* Booking Tab */}
        {activeTab === 'booking' && hasBookingInfo && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            {/* Rates */}
            {(dj.cache_min || dj.cache_max) && (
              <section className="bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-xl p-4 sm:p-6 border border-[#D4AF37]/30">
                <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <DollarSign size={18} className="text-[#D4AF37]" />
                  Tarifa
                </h2>
                <div className="flex items-baseline gap-2 flex-wrap">
                  {dj.cache_min && (
                    <span className="text-2xl sm:text-3xl font-black text-white">
                      {dj.cache_currency === 'USD' ? 'US$' : '$'}{dj.cache_min.toLocaleString()}
                    </span>
                  )}
                  {dj.cache_max && (
                    <>
                      <span className="text-white/50">-</span>
                      <span className="text-2xl sm:text-3xl font-black text-white">
                        {dj.cache_currency === 'USD' ? 'US$' : '$'}{dj.cache_max.toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-white/50 text-xs sm:text-sm mt-2">* Consultar disponibilidad y condiciones</p>
              </section>
            )}

            {/* Availability */}
            <section className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
              <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                <Calendar size={18} className="text-[#D4AF37]" />
                Disponibilidad
              </h2>
              {dj.availability && (
                <p className="text-white/80 mb-3 sm:mb-4 text-sm sm:text-base">{dj.availability}</p>
              )}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {dj.travel_available && (
                  <span className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-green-500/20 text-green-400 text-xs sm:text-sm rounded-full">
                    <Plane size={14} />
                    Disponible para viajar
                  </span>
                )}
                {dj.international_available && (
                  <span className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-blue-500/20 text-blue-400 text-xs sm:text-sm rounded-full">
                    <Globe size={14} />
                    Shows internacionales
                  </span>
                )}
              </div>
            </section>

            {/* Contact Form */}
            <section className="sm:col-span-2 bg-white/5 rounded-xl p-4 sm:p-6 border border-white/5">
              <h2 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                <Headphones size={18} className="text-[#D4AF37]" />
                Contactar para Booking
              </h2>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {dj.email_contact && (
                  <a
                    href={`mailto:${dj.email_contact}?subject=Consulta Booking - ${dj.artistic_name}`}
                    className="flex-1 flex items-center justify-center gap-2 py-3 sm:py-4 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold rounded-xl transition-colors text-sm sm:text-base"
                  >
                    Enviar Email
                    <ExternalLink size={16} />
                  </a>
                )}
                <button
                  onClick={handleShare}
                  className="flex-1 flex items-center justify-center gap-2 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-colors text-sm sm:text-base"
                >
                  <Share2 size={16} />
                  Compartir Press Kit
                </button>
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Full size" 
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
